import * as React from 'react';

import type {
  AnalysisPercentileRowDto,
  PriorAnalysisComparisonDto,
} from '@/api/campaign-analysis-client';
import { useAnalysisComparison } from '@/api/use-campaign-analysis';
import {
  usePriorEligibleCampaigns,
  type EligiblePriorCampaign,
} from '@/api/use-prior-eligible-campaigns';
import { Badge } from '@/components/ui/badge';
import { Panel } from '@/components/ui/panel';
import { formatEur, seniorityLabel } from '@/lib/operational-view-labels';
import { PanelMessage } from '@/routes/admin/operational-view/operational-view-table';

// =============================================================================
// operational-view-comparison-panel.tsx — Panneau « Comparaison avec les campagnes antérieures » (§2.5).
// =============================================================================
// V-ANALYSIS-COMPARE (DBU §2.5 l.1223-1225, ADR D10 « comparaison OPTIONNELLE avec analyses de campagnes
// antérieures »). Panneau OPTIONNEL MASQUABLE : replié par défaut, il ne déclenche AUCUNE lecture tant qu'il
// n'est pas ouvert (les hooks sont gatés par `expanded`).
// Restyling 2026-09-17 : le bouton « Afficher la comparaison » devient le lien de pied de matrice « Comparer aux
// campagnes antérieures » (OperationalViewComparisonToggle, même aria-expanded) ; l'état `expanded` est porté par
// la page et le panneau (gabarit Panel) est rendu juste sous la matrice, uniquement une fois déplié. À l'ouverture :
//   1. Résolution FRONT des campagnes antérieures éligibles (même clientSiren, Publiee) — JAMAIS de jointure
//      cross-schéma analytics↔campaign (invariant n°2). Cf. usePriorEligibleCampaigns.
//   2. Lecture analytics admin-only des analyses de ces antérieures (percentiles consolidés anonymisés,
//      K-anon par analyse) — un seul GET /admin/positioning/:id/analysis-comparison.
//
// L'AUTORITÉ reste l'API/worker : les percentiles winsorized sont CALCULÉS en amont et LUS ici. L'UI ne porte
// AUCUNE règle métier ; elle assemble l'intitulé (côté campaign) et les percentiles (côté analytics), et
// affiche le DRAPEAU de comparabilité = (catalogue de l'antérieure == catalogue courant). WCAG 2.1 AA :
// bouton de divulgation aria-expanded/aria-controls, <table>/<caption>/<th scope>, role="status"/role="alert".

export interface OperationalViewComparisonToggleProps {
  readonly expanded: boolean;
  readonly onToggle: () => void;
  /** Id de la région dépliée (aria-controls posé uniquement quand elle existe). */
  readonly controlsId: string;
}

/**
 * Lien de pied de matrice « Comparer aux campagnes antérieures » : bouton de divulgation (aria-expanded) qui
 * déplie le panneau de comparaison. Même libellé ouvert ou fermé, l'état est porté par aria-expanded.
 */
export function OperationalViewComparisonToggle({
  expanded,
  onToggle,
  controlsId,
}: OperationalViewComparisonToggleProps): React.JSX.Element {
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={expanded ? controlsId : undefined}
      onClick={onToggle}
      className="rounded text-xs font-medium text-foreground underline underline-offset-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      Comparer aux campagnes antérieures
    </button>
  );
}

export interface OperationalViewComparisonPanelProps {
  readonly campaignId: string;
  /** Panneau déplié (état porté par la page, déclenché par le lien de pied de matrice). */
  readonly expanded: boolean;
  /** Id de la région (cible aria-controls du lien). */
  readonly panelId: string;
}

export function OperationalViewComparisonPanel({
  campaignId,
  expanded,
  panelId,
}: OperationalViewComparisonPanelProps): React.JSX.Element | null {
  // Hooks gatés par `expanded` : AUCUNE lecture tant que le panneau est replié (DBU l.1225).
  const { eligible, currentCatalogVersionId, isPending, isError } =
    usePriorEligibleCampaigns(campaignId, expanded);

  const priorIds = React.useMemo(
    () => eligible.map((e) => e.campaignId),
    [eligible],
  );
  const comparisonQuery = useAnalysisComparison(
    campaignId,
    priorIds,
    {},
    expanded && priorIds.length > 0,
  );

  // Index intitulé par campaignId (l'analytics ne connaît pas l'intitulé — jointure d'AFFICHAGE côté front).
  const metaById = React.useMemo(() => {
    const map = new Map<string, EligiblePriorCampaign>();
    for (const e of eligible) {
      map.set(e.campaignId, e);
    }
    return map;
  }, [eligible]);

  if (!expanded) {
    return null;
  }

  return (
    <div id={panelId}>
      <Panel
        headingId={`${panelId}-heading`}
        title="Comparaison avec les campagnes antérieures"
        aside={
          <p className="max-w-xl text-xs">
            Comparaison optionnelle des percentiles consolidés (anonymisés) avec les
            campagnes terminées du même client.
          </p>
        }
      >
        {isError || comparisonQuery.isError ? (
          <PanelMessage kind="alert">
            Erreur de chargement de la comparaison
            {comparisonQuery.error ? ` : ${comparisonQuery.error.message}` : ''}.
          </PanelMessage>
        ) : isPending ? (
          <PanelMessage kind="status">
            Résolution des campagnes antérieures éligibles…
          </PanelMessage>
        ) : eligible.length === 0 ? (
          <PanelMessage kind="status">
            Aucune campagne antérieure comparable pour ce client (campagne terminée
            du même périmètre).
          </PanelMessage>
        ) : comparisonQuery.isPending ? (
          <PanelMessage kind="status">
            Chargement des analyses antérieures…
          </PanelMessage>
        ) : (comparisonQuery.data?.priors.length ?? 0) === 0 ? (
          <PanelMessage kind="status">
            Les campagnes antérieures éligibles n’ont pas encore d’analyse
            consolidée disponible.
          </PanelMessage>
        ) : (
          <div className="space-y-6 py-4">
            {(comparisonQuery.data?.priors ?? []).map((prior) => (
              <PriorAnalysisBlock
                key={prior.campaignId}
                prior={prior}
                intitule={metaById.get(prior.campaignId)?.intitule ?? prior.campaignId}
                comparable={prior.catalogVersionId === currentCatalogVersionId}
              />
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// --- Bloc d'une campagne antérieure (métadonnées + table percentiles consolidés) -------------------

interface PriorAnalysisBlockProps {
  readonly prior: PriorAnalysisComparisonDto;
  readonly intitule: string;
  readonly comparable: boolean;
}

function PriorAnalysisBlock({
  prior,
  intitule,
  comparable,
}: PriorAnalysisBlockProps): React.JSX.Element {
  return (
    <section className="space-y-3 px-6">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">{intitule}</h3>
        {/* DRAPEAU de comparabilité : LIBELLÉ TEXTE (jamais couleur seule, WCAG 1.4.1). */}
        {comparable ? (
          <Badge
            variant="outline"
            className="border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
          >
            Comparable (même catalogue)
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
          >
            Catalogue différent
          </Badge>
        )}
      </div>
      {prior.rows.length === 0 ? (
        <p role="status" className="text-sm text-muted-foreground">
          Aucun couple Profil × Niveau dans cette analyse.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="ds-table w-full border-collapse text-sm">
            <caption className="sr-only">
              Percentiles consolidés (P25, médiane, P75) par couple Profil × Niveau de
              la campagne antérieure « {intitule} ». Les lignes sous le seuil de
              K-anonymat sont masquées.
            </caption>
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  Profil / Niveau
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  P25
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Médiane
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  P75
                </th>
              </tr>
            </thead>
            <tbody>
              {prior.rows.map((row) => (
                <PriorPercentileRow
                  key={`${row.profileCode}-${row.seniorityLevel}`}
                  row={row}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function PriorPercentileRow({
  row,
}: {
  readonly row: AnalysisPercentileRowDto;
}): React.JSX.Element {
  return (
    <tr className="border-b border-border last:border-0">
      <th
        scope="row"
        className="px-3 py-2 text-left align-middle font-medium"
      >
        {row.profileCode} · {seniorityLabel(row.seniorityLevel)}
      </th>
      {row.suppressed ? (
        // Sous le gate K-anon : aucune valeur ne filtre — LIBELLÉ TEXTE explicite (jamais un vide muet).
        <td colSpan={3} className="px-3 py-2 text-muted-foreground">
          Masqué (K-anonymat)
        </td>
      ) : (
        <>
          <td className="px-3 py-2 text-right align-middle tabular-nums">
            {formatEur(row.p25Eur)}
          </td>
          <td className="px-3 py-2 text-right align-middle tabular-nums">
            {formatEur(row.medianEur)}
          </td>
          <td className="px-3 py-2 text-right align-middle tabular-nums">
            {formatEur(row.p75Eur)}
          </td>
        </>
      )}
    </tr>
  );
}
